import { isEqual, map } from 'lodash';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import React, { Component, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Permission, { RoleTypes } from '../../../core/components/Permission';
import { Button, Collapse } from 'reactstrap';
import {
  useForm,
  FormProvider,
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import DateTimePicker from '../../../core/components/DateTimePicker';
import {
  TextArea,
  fieldRequired,
  Input,
  requiredDatePicker,
  requiredInput,
  requiredSelect,
  Select,
  ControlledRichText,
} from '../../../core/formValidation';
import { createIntervalDescription } from '../helpers';
import { Interval, TaskType } from '../models';
import IntervalCount from './IntervalCount';
import HourlyRepeatOptions from './HourlyRepeatOptions';
import WeeklyRepeatOptions from './WeeklyRepeatOptions';
import TaskInventoryEdit from './TaskInventoryEdit';
import { TaskVideoEmbed } from './TaskVideoEmbed';
import { LinkedTaskField } from './LinkedTaskField';

const InventoryFieldArrayRHF = ({ name }) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ name, control });
  const api = {
    map: (fn) => fields.map((_, index) => fn(`${name}.${index}`, index)),
    get: (index) => fields[index],
    getAll: () => fields.map((f) => f),
    push: (item) => append(item),
    remove: (index) => remove(index),
  };
  return <TaskInventoryEdit fields={api} />;
};

const NumericTextInputRHF = ({ name, id, placeholder, disabled, min }) => {
  const { register } = useFormContext();
  return (
    <input
      id={id}
      type="text"
      className="form-control"
      inputMode="numeric"
      pattern="\\d*"
      autoComplete="off"
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      {...register(name)}
    />
  );
};

class TaskEditForm extends Component {
  state = {
    instructionsToggle: false,
    resourceToggle: false,
    linkedTaskToggle: false,
    siteDetailsToggle: false,
  };

  componentDidUpdate(prevProps) {
    const prevType = prevProps.watched?.type ?? prevProps.type;
    const currentType = this.props.watched?.type ?? this.props.type;

    if (prevType !== currentType && currentType !== TaskType.Project && this.props.rhf?.setValue) {
      this.props.rhf.setValue('prefixId', null);
    }
  }

  handleToggle = (toggle) => {
    this.setState({ [toggle]: !this.state[toggle] });
  };

  render() {
    const {
      isEssentialTask,
      taskPrefixes,
      taskLocations,
      siteOperationTypes,
      siteModelYears,
      employees,
      intl,
      completeQuickAddTask,
    } = this.props;
    const watched = this.props.watched || {};
    const interval = watched.interval ?? this.props.interval;
    const type = watched.type ?? this.props.type;
    const numericType = type === undefined || type === null ? TaskType.Maintenance : Number(type);
    const quickAddTask = watched.quickAddTask ?? this.props.quickAddTask;
    const repeatTask = watched.repeatTask ?? this.props.repeatTask;
    const intervalConfig = {
      interval: watched.interval,
      intervalCount: watched.intervalCount,
      hourlySchedule: watched.hourlySchedule,
      weeklySchedule: watched.weeklySchedule,
      monthlySameWeekday: watched.monthlySameWeekday,
      start: watched.start,
      startingCarCount: watched.startingCarCount,
      intervalCarCount: watched.intervalCarCount,
      repeatTask: watched.repeatTask,
      type: watched.type,
      prefixId: watched.prefixId,
      years: watched.years,
      linkedTasks: watched.linkedTasks,
    };
    const intervalDescription = createIntervalDescription(intervalConfig, intl.formatMessage);
    const isQuick = this.props.quickAddTask || completeQuickAddTask;
    // const intervalDescription = this.props.intervalDescription;
    const startDateText = interval === Interval.Once ? 'Deadline' : 'Start';
    const typeOptions = [
      { value: TaskType.Maintenance, text: 'Maintenance' },
      {
        value: TaskType.Project,
        text: intl.formatMessage({ id: 'workOrder', defaultMessage: 'Work Order' }),
      },
      { value: TaskType.OpenChecklist, text: 'Open Checklist' },
      { value: TaskType.ClosingChecklist, text: 'Closing Checklist' },
      {
        value: TaskType.PreventativeMaintenance,
        text: 'Preventative Maintenance',
      },
      { value: TaskType.CleaningList, text: 'Cleaning List' },
      { value: TaskType.HourlyInspection, text: 'Hourly Inspection' },
      { value: TaskType.ExpressInterior, text: 'Express Interior' },
    ];

    const intervalOptions = [
      { value: Interval.CarCount, text: 'Car Count' },
      { value: Interval.Once, text: 'Once' },
      { value: Interval.Daily, text: 'Daily' },
      { value: Interval.Weekly, text: 'Weekly' },
      { value: Interval.Monthly, text: 'Monthly' },
      { value: Interval.Yearly, text: 'Yearly' },
      { value: Interval.Hourly, text: 'Hourly' },
    ];

    let prefixesDisabled = !(numericType === TaskType.Project);
    const prefixOptions = taskPrefixes.map((prefix) => ({
      text: prefix.message,
      value: prefix.type,
    }));
    prefixOptions.unshift({ text: '', value: null });

    const locationOptions = taskLocations.map((location) => ({
      text: location.message,
      value: location.type,
    }));
    locationOptions.unshift({ text: '', value: null });

    const EmployeeOptions = employees.map((employee) => ({
      text: `${employee.lastName}, ${employee.firstName}`,
      value: employee.id,
    }));
    EmployeeOptions.unshift({ text: '', value: null });

    const operationTypeOptions = siteOperationTypes.map((operation) => ({
      text: operation.name,
      value: operation.id,
    }));
    operationTypeOptions.unshift({ text: '', value: null });

    const safeParseInt = (value) =>
      Number(value) === 0 || Number.isNaN(Number(value)) ? '' : Number(Number(value));

    const getModelYearFieldArrayProps = () => {
      const fields = this.props;
      const selectedYears = map(fields, 'yearId');

      return {
        siteModelYears,
        createdFields: fields,
        remainingModelYears: siteModelYears.filter((model) => !selectedYears.includes(model.id)),
        existingModelYears: this.props.modelYears || [],
        intl: this.props.intl,
      };
    };
    return (
      <div>
        <div className="form-title">
          {this.props.isNew
            ? intl.formatMessage({ id: 'newTaskDetails' })
            : intl.formatMessage({ id: 'editTaskDetails' })}{' '}
        </div>
        <div className="form-row flex">
          {!completeQuickAddTask && (
            <div className="form-check">
              <label className="form-check-label">
                <Controller
                  name="quickAddTask"
                  render={({ field: { value, onChange } }) => (
                    <input
                      id="quickAddTask"
                      className="form-check-input"
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  )}
                />{' '}
                {intl.formatMessage({ id: 'createWorkOrder', defaultMessage: 'Create Work Order' })}
              </label>
            </div>
          )}
          {this.props.isNew && (
            <div className="form-check">
              <label className="form-check-label">
                <Controller
                  name="addAnother"
                  render={({ field: { value, onChange } }) => (
                    <input
                      id="addAnother"
                      className="form-check-input"
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  )}
                />{' '}
                {intl.formatMessage({ id: 'addAnotherAfterSave' })}
              </label>
            </div>
          )}
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">{intl.formatMessage({ id: 'taskName' })}</label>
            <Controller
              name="name"
              render={({ field }) =>
                requiredInput({
                  input: field,
                  id: 'name',
                  type: 'text',
                  ariaDescribedBy: 'descriptionHelp',
                  placeholder: 'Enter name for task',
                })
              }
            />
          </div>
          {!quickAddTask && (
            <>
              <div className="form-group">
                <label htmlFor="type">{intl.formatMessage({ id: 'taskSubTitle' })}</label>
                <Controller
                  name="subTitle"
                  render={({ field }) => (
                    <Input
                      input={field}
                      id="subTitle"
                      type="text"
                      aria-describedby="descriptionHelp"
                      placeholder="Enter sub title for task"
                    />
                  )}
                />
              </div>
              {!prefixesDisabled && (
                <div className="form-group">
                  <label htmlFor="prefixId">{intl.formatMessage({ id: 'taskPrefix' })}</label>
                  <Controller
                    name="prefixId"
                    render={({ field: { value, onChange } }) => (
                      <Select
                        input={{
                          value,
                          onChange: (e) => onChange(Number(e?.target?.value ?? e)),
                        }}
                        id="prefixId"
                        options={prefixOptions}
                        disabled={prefixesDisabled}
                      />
                    )}
                  />
                </div>
              )}
            </>
          )}
        </div>
        {!isQuick ? (
          <>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="type">{intl.formatMessage({ id: 'type' })}</label>
                <Controller
                  name="type"
                  render={({ field: { value, onChange } }) =>
                    requiredSelect({
                      input: { value, onChange: (e) => onChange(Number(e?.target?.value ?? e)) },
                      id: 'type',
                      options: typeOptions,
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="locationId">{intl.formatMessage({ id: 'location' })}</label>
                <Controller
                  name="locationId"
                  render={({ field: { value, onChange } }) =>
                    requiredSelect({
                      input: {
                        value,
                        onChange: (e) => {
                          const raw = e?.target?.value ?? e;
                          onChange(raw ? Number(raw) : null);
                        },
                      },
                      id: 'locationId',
                      options: locationOptions,
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="employeeId">{intl.formatMessage({ id: 'teamMember' })}</label>
                <Controller
                  name="employeeId"
                  render={({ field: { value, onChange } }) =>
                    requiredSelect({
                      input: {
                        value,
                        onChange: (e) => {
                          const raw = e?.target?.value ?? e;
                          onChange(raw ? Number(raw) : null);
                        },
                      },
                      id: 'employeeId',
                      options: EmployeeOptions,
                      meta: { valid: true, touched: false },
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="priority">{intl.formatMessage({ id: 'priority' })}</label>
                <Controller
                  name="priority"
                  render={({ field }) =>
                    requiredSelect({
                      input: field,
                      id: 'priority',
                      options: [
                        { text: 'Low', value: 'Low' },
                        { text: 'Medium', value: 'Medium' },
                        { text: 'High', value: 'High' },
                        { text: 'Urgent', value: 'Urgent' },
                      ],
                      validate: [fieldRequired],
                    })
                  }
                />
              </div>

              {!this.props.isNew && (
                <div className="form-group">
                  <label htmlFor="projectStatus">{intl.formatMessage({ id: 'status' })}</label>
                  <Controller
                    name="projectStatus"
                    render={({ field }) =>
                      requiredSelect({
                        input: field,
                        id: 'projectStatus',
                        options: projectStatusOptions,
                        disabled: numericType !== TaskType.Project,
                      })
                    }
                  />
                </div>
              )}

              {!this.props.isNew ? (
                <div className="form-group">
                  <label htmlFor="taskId">{intl.formatMessage({ id: 'taskId' })}</label>
                  <Controller
                    name="id"
                    render={({ field }) => <Input input={field} id="taskId" disabled={true} />}
                  />
                </div>
              ) : (
                ''
              )}
            </div>
          </>
        ) : (
          <div className="collapse-padding">
            <div className="form-row">
              {!prefixesDisabled && (
                <div className="form-group">
                  <label htmlFor="prefixId">{intl.formatMessage({ id: 'taskPrefix' })}</label>
                  <Controller
                    name="prefixId"
                    render={({ field: { value, onChange } }) => (
                      <Select
                        input={{
                          value,
                          onChange: (e) => onChange(Number(e?.target?.value ?? e)),
                        }}
                        id="prefixId"
                        options={prefixOptions}
                        disabled={prefixesDisabled}
                      />
                    )}
                  />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="start">{intl.formatMessage({ id: 'dueDate' })}</label>
                <Controller
                  name="dueDate"
                  render={({ field }) =>
                    requiredDatePicker({
                      input: field,
                      id: 'dueDate',
                      placeholderText: 'Choose date',
                      showToday: true,
                      meta: { valid: true, touched: false },
                      validate: [fieldRequired],
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="priority">{intl.formatMessage({ id: 'priority' })}</label>
                <Controller
                  name="priority"
                  render={({ field }) =>
                    requiredSelect({
                      input: field,
                      id: 'priority',
                      options: [
                        { text: 'Low', value: 'Low' },
                        { text: 'Medium', value: 'Medium' },
                        { text: 'High', value: 'High' },
                        { text: 'Urgent', value: 'Urgent' },
                      ],
                    })
                  }
                />
              </div>
              {!this.props.isNew && (
                <div className="form-group">
                  <label htmlFor="projectStatus">{intl.formatMessage({ id: 'status' })}</label>
                  <Controller
                    name="projectStatus"
                    render={({ field }) =>
                      requiredSelect({
                        input: field,
                        id: 'projectStatus',
                        options: projectStatusOptions,
                        disabled: numericType !== TaskType.Project,
                      })
                    }
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="locationId">{intl.formatMessage({ id: 'location' })}</label>
                <Controller
                  name="locationId"
                  render={({ field: { value, onChange } }) =>
                    requiredSelect({
                      input: {
                        value,
                        onChange: (e) => {
                          const raw = e?.target?.value ?? e;
                          onChange(raw ? Number(raw) : null);
                        },
                      },
                      id: 'locationId',
                      options: locationOptions,
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="employeeId">{intl.formatMessage({ id: 'teamMember' })}</label>
                <Controller
                  name="employeeId"
                  render={({ field: { value, onChange } }) => (
                    <Select
                      input={{
                        value,
                        onChange: (e) => {
                          const raw = e?.target?.value ?? e;
                          onChange(raw ? Number(raw) : null);
                        },
                      }}
                      id="employeeId"
                      options={EmployeeOptions}
                    />
                  )}
                />
              </div>
            </div>
            <label htmlFor="description">{intl.formatMessage({ id: 'description' })}</label>
            <Controller
              name="description"
              render={({ field }) => (
                <ControlledRichText
                  input={field}
                  meta={{ initial: this.props.initialValues?.description }}
                  id="description"
                  placeholder=""
                />
              )}
            />
          </div>
        )}
        {!isQuick ? (
          <div>
            <div className="form-title">{intl.formatMessage({ id: 'interval' })}</div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="interval">
                  {intl.formatMessage({ id: 'dueDateIntervalType' })}
                </label>
                <Controller
                  name="interval"
                  render={({ field: { value, onChange } }) =>
                    requiredSelect({
                      input: {
                        value,
                        onChange: (e) => {
                          const raw = e?.target?.value ?? e;
                          onChange(raw ? Number(raw) : null);
                        },
                      },
                      id: 'interval',
                      options: intervalOptions,
                    })
                  }
                />
              </div>
              <div className="form-group interval-description">
                <strong>{intervalDescription}</strong>
              </div>
            </div>
            {/* additionalInterval selection removed during RHF migration */}
            <div className="form-row">
              {interval !== Interval.CarCount && (
                <>
                  {/* <div className="form-group">
                <label htmlFor="start">{startDateText}</label>
                <Field
                  name="additionalInterval"
                  id="additionalInterval"
                  component={requiredDatePicker}
                  placeholderText="Choose date"
                  showToday
                  validate={[fieldRequired]}
                />
              </div> */}
                  <div className="form-group">
                    <label htmlFor="start">{startDateText}</label>
                    <Controller
                      name="start"
                      render={({ field }) =>
                        requiredDatePicker({
                          input: field,
                          id: 'start',
                          placeholderText: 'Choose date',
                          showToday: true,
                        })
                      }
                    />
                  </div>
                  {interval !== Interval.Once && (
                    <div className="form-group">
                      <label htmlFor="end">End</label>
                      <Controller
                        name="end"
                        render={({ field }) => (
                          <DateTimePicker
                            input={field}
                            id="end"
                            placeholderText="Choose date"
                            showToday={false}
                          />
                        )}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="form-row full">
              {/* {interval === Interval.Daily && ( */}
              {(interval === Interval.Daily ||
                interval === Interval.Monthly ||
                interval === Interval.Yearly) && (
                <Controller
                  name="intervalCount"
                  render={({ field }) => <IntervalCount input={field} />}
                />
              )}
              {interval === Interval.Weekly && (
                <Controller
                  name="weeklySchedule"
                  render={({ field }) => <WeeklyRepeatOptions input={field} />}
                />
              )}
              {interval === Interval.CarCount && (
                <>
                  <div className="form-group" key="0">
                    <label htmlFor="startingCarCount" className="mr-1">
                      Starting car count
                    </label>
                    <NumericTextInputRHF
                      name="startingCarCount"
                      id="startingCarCount"
                      placeholder="When the task should be completed for the first time"
                      min="1"
                    />
                  </div>

                  {!isEssentialTask && (
                    <div className="form-group" key="1">
                      <label htmlFor="carCount" className="mr-1">
                        {this.props.intl.formatMessage(
                          { id: 'currentCarCount' },
                          { carCount: this.props.totalCarCount }
                        )}{' '}
                        {`(${moment().format('MM/DD/YYYY')})`}
                      </label>
                    </div>
                  )}
                  <div className="form-group" key="2">
                    <div className="form-check">
                      <label className="form-check-label">
                        <Controller
                          name="repeatTask"
                          render={({ field: { value, onChange } }) => (
                            <input
                              id="repeatTask"
                              className="form-check-input"
                              type="checkbox"
                              checked={!!value}
                              onChange={(e) => onChange(e.target.checked)}
                            />
                          )}
                        />{' '}
                        {intl.formatMessage({ id: 'repeatTask' })}
                      </label>
                    </div>
                  </div>
                  <div className="form-group" key="3">
                    <label htmlFor="intervalCarCount" className="mr-1">
                      {intl.formatMessage({ id: 'repeatInterval' })}
                    </label>
                    <NumericTextInputRHF
                      name="intervalCarCount"
                      id="intervalCarCount"
                      placeholder="Car count for repeating task"
                      disabled={!this.props.repeatTask}
                      min="1"
                    />
                  </div>
                </>
              )}
              {interval === Interval.Hourly && (
                <Controller
                  name="hourlySchedule"
                  render={({ field }) => (
                    <HourlyRepeatOptions input={field} hours={field.value || []} />
                  )}
                />
              )}
            </div>
            <div
              onClick={() => this.handleToggle('instructionsToggle')}
              className="form-title collapsible"
            >
              <span>{intl.formatMessage({ id: 'instructions' })}</span>
              <Button style={{ border: '0px' }}>
                <i className="icon icon-chevron-down" />
              </Button>
            </div>
            <Collapse isOpen={this.state.instructionsToggle}>
              <div className="collapse-padding">
                <Controller
                  name="description"
                  render={({ field }) => (
                    <ControlledRichText
                      input={field}
                      meta={{ initial: this.props.initialValues?.description }}
                      id="description"
                      placeholder=""
                    />
                  )}
                />
              </div>
            </Collapse>
            <div
              onClick={() => this.handleToggle('linkedTaskToggle')}
              className="form-title collapsible"
            >
              <span>
                {intl.formatMessage({ id: 'linkTasks' })}
                {this.props?.linkedTasks?.length ? ` (+${this.props?.linkedTasks?.length})` : ''}
              </span>
              <Button style={{ border: '0px' }}>
                <i className="icon icon-chevron-down" />
              </Button>
            </div>
            <Collapse isOpen={this.state.linkedTaskToggle}>
              <div className="collapse-padding linked-tasks">
                <LinkedTaskField
                  name="linkedTasks"
                  tasks={this.props.tasks}
                  intl={this.props.intl}
                />
              </div>
            </Collapse>

            <div
              onClick={() => this.handleToggle('resourceToggle')}
              className="form-title collapsible"
            >
              <span>
                {this.props.intl.formatMessage({ id: 'videos' })} &{' '}
                {intl.formatMessage({ id: 'resources' })}
              </span>
              <Button style={{ border: '0px' }}>
                <i className="icon icon-chevron-down" />
              </Button>
            </div>

            <Collapse isOpen={this.state.resourceToggle}>
              <div className="form-row full">
                <div className="form-group">
                  <label htmlFor="videos">{this.props.intl.formatMessage({ id: 'videos' })}</label>
                  <TaskVideoEmbed name="videos" />
                </div>
                {isEssentialTask && (
                  <div className="form-group">
                    <label htmlFor="resource" className="mt-3">
                      {this.props.intl.formatMessage({ id: 'resource' })}
                    </label>
                    <Controller
                      name="resource"
                      render={({ field }) => (
                        <Input
                          input={field}
                          id="resource"
                          type="text"
                          placeholder="Enter resource url"
                        />
                      )}
                    />
                  </div>
                )}
              </div>
              {!isEssentialTask && (
                <div className="form-row full">
                  <div className="form-group">
                    <label htmlFor="inventory">Parts</label>
                    <InventoryFieldArrayRHF name="inventory" />
                  </div>
                </div>
              )}
            </Collapse>

            <Permission role={RoleTypes.OperatorGroupAdmin} minRole={RoleTypes.OperatorGroupAdmin}>
              {isEssentialTask && (
                <>
                  <div
                    onClick={() => this.handleToggle('siteDetailsToggle')}
                    className="form-title collapsible"
                  >
                    <span>{intl.formatMessage({ id: 'siteDetails' })}</span>
                    <Button style={{ border: '0px' }}>
                      <i className="icon icon-chevron-down" />
                    </Button>
                  </div>
                  <Collapse isOpen={this.state.siteDetailsToggle}>
                    <div className="form-row half">
                      <div className="form-group">
                        <label htmlFor="name">Operation Type</label>
                        <Controller
                          name="operationTypeId"
                          render={({ field: { value, onChange } }) =>
                            requiredSelect({
                              input: { value, onChange: (v) => onChange(v && Number(v)) },
                              id: 'operationTypeId',
                              options: operationTypeOptions,
                            })
                          }
                        />
                      </div>
                    </div>
                  </Collapse>
                </>
              )}
            </Permission>
          </div>
        ) : (
          ''
        )}
      </div>
    );
  }
}

TaskEditForm.propTypes = {
  interval: PropTypes.number,
  intervalDescription: PropTypes.string,
  hours: PropTypes.arrayOf(PropTypes.number),
  totalCarCount: PropTypes.number,
  intl: PropTypes.shape({}).isRequired,
  repeatTask: PropTypes.bool,
  isEssentialTask: PropTypes.bool,
  type: PropTypes.number,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  taskPrefixes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.number,
      message: PropTypes.string,
    })
  ),
  taskLocations: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.number,
      message: PropTypes.string,
      orderNumber: PropTypes.number,
    })
  ),
  modelYears: PropTypes.arrayOf(
    PropTypes.shape({
      yearId: PropTypes.number,
    })
  ),
  siteModelYears: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      year: PropTypes.string,
    })
  ),
  siteOperationTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    })
  ),
};

const ConnectedTaskEditForm = compose(
  injectIntl,
  connect(() => ({}))
)(TaskEditForm);

const TaskEditFormWithRHF = forwardRef((props, ref) => {
  const methods = useForm({ defaultValues: props.initialValues || {} });
  const prevInitialValues = useRef(props.initialValues);
  const watchedArray = useWatch({
    control: methods.control,
    name: [
      'interval',
      'intervalCount',
      'hourlySchedule',
      'weeklySchedule',
      'monthlySameWeekday',
      'start',
      'startingCarCount',
      'intervalCarCount',
      'repeatTask',
      'type',
      'prefixId',
      'years',
      'linkedTasks',
      'quickAddTask',
      'projectStatus',
    ],
  });
  const watched = {
    interval: watchedArray?.[0],
    intervalCount: watchedArray?.[1],
    hourlySchedule: watchedArray?.[2],
    weeklySchedule: watchedArray?.[3],
    monthlySameWeekday: watchedArray?.[4],
    start: watchedArray?.[5],
    startingCarCount: watchedArray?.[6],
    intervalCarCount: watchedArray?.[7],
    repeatTask: watchedArray?.[8],
    type: watchedArray?.[9],
    prefixId: watchedArray?.[10],
    years: watchedArray?.[11],
    linkedTasks: watchedArray?.[12],
    quickAddTask: watchedArray?.[13],
    projectStatus: watchedArray?.[14],
  };
  useEffect(() => {
    if (!props.enableReinitialize) return;
    if (!isEqual(prevInitialValues.current, props.initialValues)) {
      methods.reset({ ...props.initialValues });
      prevInitialValues.current = props.initialValues;
    }
  }, [props.enableReinitialize, props.initialValues, methods]);
  useImperativeHandle(ref, () => ({
    values: () => {
      return methods.getValues();
    },
    submit: () => methods.handleSubmit((vals) => props.onSubmit && props.onSubmit(vals))(),
    reset: (values) =>
      methods.reset({
        //quick add and add another are persistent
        quickAddTask: values?.quickAddTask,
        addAnother: values?.addAnother,
        ...props.initialValues,
      }),
  }));
  return (
    <FormProvider {...methods}>
      <ConnectedTaskEditForm {...props} rhf={methods} watched={watched} />
    </FormProvider>
  );
});

export default TaskEditFormWithRHF;
const projectStatusOptions = [
  { value: 'Backlog', text: 'Backlog' },
  { value: 'Planning', text: 'Planning' },
  { value: 'In Progress', text: 'In Progress' },
  { value: 'Review', text: 'Review' },
  { value: 'Completed', text: 'Completed' },
];
